import { ReactNode, useState } from 'react';

import { cn } from '@/components/ui/cn';
import { SelectOption, SelectPopover } from '@/components/ui/SelectPopover';
import { Popover, menuRow } from '@/components/ui/Popover';
import { inputCls } from '@/components/ui/form';
import { DatePicker } from '@/components/ui/DatePicker';
import { Calendar, Check, ChevronDown, Plus, PriorityChevron, SubItem, Tag, TaskTypeGlyph } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import StatusDot from '@/components/status-dot';
import { useMemberCreate } from '@/components/new-member';
import { Item, Member, Priority, TaskType } from '@/features/backlog/types';
import { PRIORITIES, TASK_TYPES, priorityMeta, taskTypeMeta } from '@/features/backlog/constants';
import { StatusMeta } from '@/features/backlog/statuses';

/**
 * The item drawer's "properties" controls: one shared row-button per single-value field
 * (status / priority / assignee / PdM / dates / parent), each opening the SAME popover the
 * kanban cards use — same `menuRow` bodies, same status dot / {@link PriorityChevron} /
 * {@link MemberAvatar} / {@link DatePicker} tokens — so editing in the drawer feels identical
 * to editing on the board. They're meant to stack inside one `overflow-hidden rounded-xl
 * border` card; each row draws its own top hairline (first row suppresses it).
 *
 * Design: a fixed label gutter + a fixed 20px token slot line labels, tokens and values into
 * three straight columns so a whole record scans in one pass. At rest a row is quiet — only the
 * coloured token carries hue; hover fills the row and fades in the trailing chevron. Every
 * onChange routes through the drawer's debounced `set(field, value)` autosave (no Save button),
 * and each handler skips a no-op re-pick so it never writes a bare "saved" activity entry.
 */

// The row-button shell, shared by every property control. `group` drives the chevron reveal; the
// focus ring is INSET (+ relative/z-10) so the card's `overflow-hidden` can't clip it on the
// first/last row.
const rowBtn =
  'group flex min-h-[48px] w-full items-center gap-3 px-3 text-left text-[13px] transition-colors ' +
  'hover:bg-layer focus-visible:relative focus-visible:z-10 focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/60';
const gutterCls = 'w-[72px] shrink-0 text-[11px] font-semibold uppercase tracking-wide text-fg-faint';
const tokenCls = 'flex h-5 w-5 shrink-0 items-center justify-center'; // fixed 20px slot aligns every token
const valueCls = (isSet: boolean) => cn('min-w-0 flex-1 truncate', isSet ? 'font-medium text-fg' : 'text-fg-ghost');
const chevCls = (open: boolean) =>
  cn(
    'shrink-0 text-fg-ghost opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
    open && 'opacity-100',
  );
// The hairline lives on this per-row wrapper (not the trigger button): Popover/DatePicker each
// wrap their trigger in an extra `relative` div, so `first:` on the button itself would misfire.
const rowWrap = 'border-t border-line first:border-t-0';

/** The inner content of a row-button (gutter label · token · value · trailing chevron). */
function RowButtonInner(props: { open: boolean; label: string; token: ReactNode; value: ReactNode; isSet: boolean }) {
  const { open, label, token, value, isSet } = props;
  return (
    <>
      <span className={gutterCls}>{label}</span>
      <span className={tokenCls}>{token}</span>
      <span className={valueCls(isSet)}>{value}</span>
      <ChevronDown open={open} className={chevCls(open)} />
    </>
  );
}

/** Status — a coloured dot + label, opening the searchable status list. */
export function StatusRow(props: {
  value: string;
  meta: StatusMeta;
  statuses: { value: string; label: string; color: string }[];
  onChange: (value: string) => void;
  /** Optional deep-link anchor (`data-hl`), so a notification can scroll to + flash this row. */
  hlId?: string;
}) {
  const { value, meta, statuses, onChange } = props;
  // Keep a briefly out-of-sync value (e.g. its column was deleted elsewhere) selectable.
  const outOfSync = !!value && !statuses.some((s) => s.value === value);
  const list = outOfSync ? [{ value: meta.value, label: meta.label, color: meta.color }, ...statuses] : statuses;
  const options: SelectOption[] = list.map((s) => ({
    value: s.value,
    label: s.label,
    leading: <StatusDot value={s.value} size={16} />,
  }));
  return (
    <div className={rowWrap} data-hl={props.hlId}>
      <SelectPopover
        options={options}
        value={value}
        onChange={onChange}
        searchPlaceholder="Search statuses…"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Status: ${meta.label}. Change status`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner
              open={open}
              label="Status"
              token={<StatusDot value={value} size={16} />}
              value={meta.label}
              isSet
            />
          </button>
        )}
      />
    </div>
  );
}

/** Priority — the fixed low/medium/high/blocker chevron mark + label. */
export function PriorityRow(props: { value: Priority; onChange: (value: Priority) => void; hlId?: string }) {
  const { value, onChange } = props;
  const pm = priorityMeta(value);
  const options: SelectOption[] = PRIORITIES.map((p) => ({
    value: p.value,
    label: p.label,
    leading: <PriorityChevron level={p.level} color={p.color} />,
  }));
  return (
    <div className={rowWrap} data-hl={props.hlId}>
      <SelectPopover
        options={options}
        value={value}
        onChange={(v) => onChange(v as Priority)}
        searchPlaceholder="Search priority…"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Priority: ${pm.label}. Change priority`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner open={open} label="Priority" token={<PriorityChevron level={pm.level} color={pm.color} />} value={pm.label} isSet />
          </button>
        )}
      />
    </div>
  );
}

/** Type — the fixed Initiative/Epic/Story/Task/Bug glyph badge + label. */
export function TypeRow(props: { value: TaskType; onChange: (value: TaskType) => void }) {
  const { value, onChange } = props;
  const tm = taskTypeMeta(value);
  const options: SelectOption[] = TASK_TYPES.map((t) => ({
    value: t.value,
    label: t.label,
    leading: <TaskTypeGlyph type={t.value} color={t.color} />,
  }));
  return (
    <div className={rowWrap}>
      <SelectPopover
        options={options}
        value={value}
        onChange={(v) => onChange(v as TaskType)}
        searchPlaceholder="Search type…"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Type: ${tm.label}. Change type`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner open={open} label="Type" token={<TaskTypeGlyph type={tm.value} color={tm.color} />} value={tm.label} isSet />
          </button>
        )}
      />
    </div>
  );
}

/** A member field (assignee or PdM) — mnemonic avatar + name, with an "unset" row on top. */
export function MemberRow(props: {
  label: string;
  value: string;
  members: Member[];
  onChange: (id: string) => void;
  /** Value + first menu row when nobody is set (e.g. "Unassigned" / "No PdM"). */
  emptyLabel: string;
  /** Noun for the search placeholder / aria (defaults to the lowercased label; pass to keep
   *  correct casing like "PdM"). */
  noun?: string;
  /** Optional deep-link anchor (`data-hl`), so a notification can scroll to + flash this row. */
  hlId?: string;
}) {
  const { label, value, members, onChange, emptyLabel } = props;
  const noun = props.noun ?? label.toLowerCase();
  const member = members.find((m) => m.id === value);
  const memberCreate = useMemberCreate();
  const options: SelectOption[] = [
    { value: '', label: emptyLabel, leading: <MemberAvatar member={undefined} size={20} />, tone: 'text-fg-muted' },
    ...members.map((m) => ({
      value: m.id,
      label: m.name,
      leading: <MemberAvatar member={m} size={20} />,
      keywords: m.email ?? '',
    })),
  ];
  return (
    <div className={rowWrap} data-hl={props.hlId}>
      <SelectPopover
        options={options}
        value={value}
        onChange={onChange}
        {...memberCreate(onChange)}
        searchPlaceholder={`Search ${noun}…`}
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`${label}: ${member ? member.name : emptyLabel}. Change ${noun}`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner
              open={open}
              label={label}
              token={<MemberAvatar member={member} size={20} />}
              value={member ? member.name : emptyLabel}
              isSet={!!member}
            />
          </button>
        )}
      />
    </div>
  );
}

/** Reporter — who created the item, and when. Read-only: same gutter/token/value columns as the
 *  editable rows, but a plain row (no hover fill, no chevron) since there's nothing to pick. The
 *  exact timestamp sits in the tooltip. */
export function ReporterRow(props: { member: Member | undefined; createdAgo: string; createdTitle: string }) {
  const { member, createdAgo, createdTitle } = props;
  return (
    <div className={rowWrap}>
      <div className="flex min-h-[48px] w-full items-center gap-3 px-3 text-[13px]" title={`Created ${createdTitle}`}>
        <span className={gutterCls}>Reporter</span>
        <span className={tokenCls}>
          <MemberAvatar member={member} size={20} />
        </span>
        <span className={valueCls(!!member)}>{member ? member.name : 'Unknown'}</span>
        <span className="shrink-0 text-[12px] text-fg-ghost">{createdAgo}</span>
      </div>
    </div>
  );
}

/** A date field — reuses {@link DatePicker} (calendar / flip / weekend / Clear untouched) via a
 *  custom trigger so it wears the row-button shell. Kept visually neutral to match the board's
 *  DateCell (which never colours a past date); a real overdue signal would belong board-wide. */
export function DateRow(props: {
  label: string;
  heading: string;
  value: string; // '' or 'YYYY-MM-DD'
  onChange: (value: string) => void;
  align?: 'left' | 'right';
}) {
  const { label, heading, value, onChange, align } = props;
  return (
    <div className={rowWrap}>
      <DatePicker
        value={value || null}
        onChange={(v) => onChange(v ?? '')}
        heading={heading}
        align={align}
        trigger={({ open, toggle, label: dateLabel }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`${heading}: ${dateLabel || 'none'}. Change date`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <span className={gutterCls}>{label}</span>
            <span className={tokenCls}>
              <Calendar className={dateLabel ? 'text-fg-faint' : 'text-fg-ghost'} />
            </span>
            <span className={valueCls(!!dateLabel)}>{dateLabel || 'Add date'}</span>
            <ChevronDown open={open} className={chevCls(open)} />
          </button>
        )}
      />
    </div>
  );
}

/** Parent picker — a searchable list (the atom that can grow long), each item tagged with its
 *  own status colour for context. */
export function ParentRow(props: {
  value: string;
  options: Item[];
  statusColor: (status: string) => string;
  onChange: (id: string) => void;
}) {
  const { value, options, statusColor, onChange } = props;
  const parent = options.find((it) => it.id === value);
  const opts: SelectOption[] = [
    { value: '', label: 'None (top level)', leading: <span className="h-2 w-2 rounded-full bg-fg-ghost/60" />, tone: 'text-fg-muted' },
    ...options.map((it) => ({
      value: it.id,
      label: it.title,
      leading: <span className="h-2 w-2 rounded-full" style={{ background: statusColor(it.status) }} />,
    })),
  ];
  return (
    <div className={rowWrap}>
      <SelectPopover
        options={opts}
        value={value}
        onChange={onChange}
        searchPlaceholder="Search items…"
        emptyText={(query) => `No items match “${query}”`}
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Parent item: ${parent ? parent.title : 'top level'}. Change parent`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner
              open={open}
              label="Parent"
              token={<SubItem className="h-4 w-4 text-fg-faint" />}
              value={parent ? parent.title : 'Top level'}
              isSet={!!parent}
            />
          </button>
        )}
      />
    </div>
  );
}

/** A MULTI-select of free-form string tags (Source, Labels), styled as a table row to match the others.
 *  Search the known/used values, tick any number, or type a new one and "Create" it in-place. */
function TagMultiRow(props: {
  title: string;
  searchPlaceholder: string;
  addLabel: string;
  emptyHint: string;
  value: string[];
  options: string[];
  onChange: (next: string[]) => void;
}) {
  const { title, searchPlaceholder, addLabel, emptyHint, value, options, onChange } = props;
  const [q, setQ] = useState('');
  // Selected values not in the known/discovered set still render (as ticked), so a freshly-created
  // source shows immediately even before it's saved + rediscovered.
  const all = Array.from(new Set([...options, ...value]));
  const needle = q.trim().toLowerCase();
  const filtered = needle ? all.filter((o) => o.toLowerCase().includes(needle)) : all;
  const showCreate = !!q.trim() && !all.some((o) => o.toLowerCase() === needle);
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const add = (v: string) => {
    const t = v.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setQ('');
  };

  return (
    <div className={rowWrap}>
      <Popover
        portal
        menu
        trigger={({ open, toggle: openToggle }) => (
          <button
            type="button"
            onClick={() => {
              if (!open) setQ('');
              openToggle();
            }}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`${title}: ${value.length ? value.join(', ') : 'none'}. Edit ${title.toLowerCase()}`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner
              open={open}
              label={title}
              token={<Tag className={value.length ? 'text-fg-faint' : 'text-fg-ghost'} />}
              value={value.length ? value.join(', ') : addLabel}
              isSet={value.length > 0}
            />
          </button>
        )}
      >
        {(close) => (
          <>
            <div className="-mx-1.5 -mt-1.5 mb-1 border-b border-line p-1.5">
              <input
                role="combobox"
                aria-expanded
                aria-autocomplete="list"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className={cn(inputCls, 'h-9')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && showCreate) {
                    e.preventDefault();
                    add(q);
                  }
                }}
              />
            </div>
            <div role="listbox" aria-multiselectable className="thin-scroll -mx-1.5 max-h-[240px] overflow-auto px-1.5">
              {filtered.map((o) => {
                const checked = value.includes(o);
                return (
                  <button key={o} type="button" role="option" aria-selected={checked} onClick={() => toggle(o)} className={cn(menuRow, 'text-fg-soft')}>
                    <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', checked ? 'border-accent bg-accent text-black' : 'border-line')}>
                      {checked ? <Check className="h-2.5 w-2.5" /> : null}
                    </span>
                    <span className="flex-1 truncate">{o}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && !showCreate ? (
                <div className="px-2 py-6 text-center text-[12px] text-fg-ghost">{emptyHint}</div>
              ) : null}
              {showCreate ? (
                <button type="button" onClick={() => add(q)} className={cn(menuRow, 'text-accent')}>
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <Plus />
                  </span>
                  <span className="flex-1 truncate">Create “{q.trim()}”</span>
                </button>
              ) : null}
            </div>
            {value.length ? (
              <button
                type="button"
                onClick={() => {
                  onChange([]);
                  close();
                }}
                className="mt-1 flex min-h-[40px] w-full items-center rounded-lg border-t border-line px-2.5 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
              >
                Clear all
              </button>
            ) : null}
          </>
        )}
      </Popover>
    </div>
  );
}

/** Source — free-form provenance tags (per-view discovered options). */
export function SourceRow(props: { value: string[]; options: string[]; onChange: (next: string[]) => void }) {
  return <TagMultiRow title="Source" searchPlaceholder="Search or create source…" addLabel="Add source" emptyHint="No sources yet — type to create one." {...props} />;
}

/** Labels — free-form, workspace-global tags (options come from the shared /labels set). */
export function LabelsRow(props: { value: string[]; options: string[]; onChange: (next: string[]) => void }) {
  return <TagMultiRow title="Labels" searchPlaceholder="Search or create label…" addLabel="Add label" emptyHint="No labels yet — type to create one." {...props} />;
}

// ---- Collapsed summary (drawer) --------------------------------------------
// A compact one-line echo of the card's key properties — status · assignee · end date — shown
// when the drawer's full property table is collapsed, so description + attachments lead. Each chip
// opens the SAME picker as its full row, so a quick status/assignee/date change never needs the
// table expanded. Mirrors the kanban card footer, so the drawer's at-a-glance line reads the same.
const summaryChip =
  'inline-flex max-w-full items-center gap-1.5 rounded-full border border-line bg-surface-inset px-2.5 py-1 ' +
  'text-[12px] transition-colors hover:bg-layer focus-visible:relative focus-visible:z-10 focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/60';

export function PropertySummary(props: {
  status: string;
  statusMeta: StatusMeta;
  statuses: { value: string; label: string; color: string }[];
  onStatus: (value: string) => void;
  assigneeId: string;
  members: Member[];
  onAssignee: (id: string) => void;
  endDate: string;
  onEndDate: (value: string) => void;
}) {
  const { status, statusMeta, statuses, onStatus, assigneeId, members, onAssignee, endDate, onEndDate } = props;
  const member = members.find((m) => m.id === assigneeId);

  // Keep a briefly out-of-sync status (its column deleted elsewhere) selectable, as StatusRow does.
  const outOfSync = !!status && !statuses.some((s) => s.value === status);
  const statusList = outOfSync ? [{ value: statusMeta.value, label: statusMeta.label, color: statusMeta.color }, ...statuses] : statuses;
  const statusOpts: SelectOption[] = statusList.map((s) => ({
    value: s.value,
    label: s.label,
    leading: <StatusDot value={s.value} size={16} />,
  }));
  const memberOpts: SelectOption[] = [
    { value: '', label: 'Unassigned', leading: <MemberAvatar member={undefined} size={18} />, tone: 'text-fg-muted' },
    ...members.map((m) => ({ value: m.id, label: m.name, leading: <MemberAvatar member={m} size={18} />, keywords: m.email ?? '' })),
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SelectPopover
        options={statusOpts}
        value={status}
        onChange={onStatus}
        searchPlaceholder="Search statuses…"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Status: ${statusMeta.label}. Change status`}
            className={cn(summaryChip, open && 'bg-layer')}
          >
            <StatusDot value={status} size={16} />
            <span className="truncate font-medium text-fg">{statusMeta.label}</span>
          </button>
        )}
      />
      <SelectPopover
        options={memberOpts}
        value={assigneeId}
        onChange={onAssignee}
        searchPlaceholder="Search assignee…"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Assignee: ${member ? member.name : 'Unassigned'}. Change assignee`}
            className={cn(summaryChip, open && 'bg-layer')}
          >
            <MemberAvatar member={member} size={18} />
            <span className={cn('truncate', member ? 'font-medium text-fg' : 'text-fg-muted')}>{member ? member.name : 'Unassigned'}</span>
          </button>
        )}
      />
      <DatePicker
        value={endDate || null}
        onChange={(v) => onEndDate(v ?? '')}
        heading="End date"
        align="right"
        trigger={({ open, toggle, label }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`End date: ${label || 'none'}. Change end date`}
            className={cn(summaryChip, open && 'bg-layer')}
          >
            <Calendar className={cn('shrink-0', label ? 'text-fg-faint' : 'text-fg-ghost')} />
            <span className={cn('truncate', label ? 'font-medium text-fg' : 'text-fg-muted')}>{label || 'No end date'}</span>
          </button>
        )}
      />
    </div>
  );
}

/** Epic link picker — a searchable list of the board's Epic-typed items (matchable by key or
 *  title), independent of the Parent field. "None" clears the link. When `onCreate` is given,
 *  typing an unmatched name offers a "Create epic" row that adds it in-place and links it. */
export function EpicRow(props: {
  value: string;
  options: Item[];
  onChange: (id: string) => void;
  onCreate?: (title: string) => void;
}) {
  const { value, options, onChange, onCreate } = props;
  const epic = options.find((it) => it.id === value);
  const epicColor = taskTypeMeta('epic').color;
  const opts: SelectOption[] = [
    { value: '', label: 'None', leading: <span className="h-2 w-2 rounded-full bg-fg-ghost/60" />, tone: 'text-fg-muted' },
    ...options.map((it) => ({
      value: it.id,
      label: it.title,
      keywords: it.key,
      leading: <TaskTypeGlyph type="epic" color={epicColor} />,
    })),
  ];
  return (
    <div className={rowWrap}>
      <SelectPopover
        options={opts}
        value={value}
        onChange={onChange}
        searchPlaceholder="Search or create epic…"
        emptyText={(query) => `No epics match “${query}”`}
        onCreate={onCreate}
        createLabel={(query) => `Create epic “${query}”`}
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Epic: ${epic ? epic.title : 'none'}. Change epic link`}
            className={cn(rowBtn, open && 'bg-layer')}
          >
            <RowButtonInner
              open={open}
              label="Epic"
              token={<TaskTypeGlyph type="epic" color={epicColor} className={epic ? undefined : 'opacity-40'} />}
              value={epic ? epic.title : 'None'}
              isSet={!!epic}
            />
          </button>
        )}
      />
    </div>
  );
}
